import React, { useState } from "react"
import { AddressElement, useElements } from "@stripe/react-stripe-js"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import Input from "aldoo-ra/Input"
import AldooStoreAPI from "aldoo-ra/Store/api"

const validateTaxIdWithStripe = async (taxId, country) => {
  if (!taxId || !country) return { valid: true, error: null }

  try {
    const response = await AldooStoreAPI.validateTaxId({ taxId, country })

    if (response.result && response.valid) {
      return { valid: true, error: null }
    }

    return {
      valid: false,
      error: response.error || 'Invalid tax ID format'
    }
  } catch (error) {
    console.error('Tax ID validation error:', error)
    return {
      valid: false,
      error: 'Unable to validate tax ID'
    }
  }
}

const InvoiceDetailsDialog = ({ store, onBack, onContinue }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const elements = useElements()
  const [needsInvoice, setNeedsInvoice] = useState(false)
  const [companyName, setCompanyName] = useState("")
  const [taxId, setTaxId] = useState("")
  const [addressComplete, setAddressComplete] = useState(false)
  const [currentCountry, setCurrentCountry] = useState(null)
  const [taxIdError, setTaxIdError] = useState(null)

  const handleSubmit = async (e) => {
    e?.preventDefault()

    let address = null
    let country = currentCountry

    if (needsInvoice && elements) {
      const addressElement = elements.getElement('address')
      if (addressElement) {
        const { complete, value: addressValue } = await addressElement.getValue()
        if (complete && addressValue) {
          country = addressValue.address.country
          address = {
            line1: addressValue.address.line1,
            line2: addressValue.address.line2,
            city: addressValue.address.city,
            state: addressValue.address.state,
            postal_code: addressValue.address.postal_code,
            country: addressValue.address.country,
          }
        }
      }
    }

    if (taxId && country) {
      const validation = await validateTaxIdWithStripe(taxId, country)
      if (!validation.valid) {
        setTaxIdError(validation.error)
        return
      }
    }

    onContinue({
      needsInvoice,
      companyName,
      taxId,
      address,
      addressComplete
    })
  }

  const handleTaxIdChange = (e) => {
    const newValue = e.target.value.toUpperCase()
    setTaxId(newValue)
    if (taxIdError) setTaxIdError(null)
  }

  const canContinue = !needsInvoice || (companyName && addressComplete)

  const getMissingFields = () => {
    if (!needsInvoice) return []
    const missing = []
    if (!companyName) missing.push("Company Name")
    if (!addressComplete) missing.push("Billing Address")
    return missing
  }

  const missingFields = getMissingFields()

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full max-w-[600px] mx-auto">
        <div className="pt-4 pb-2">
          <Typography variant="body1_accent" className="text-text mb-4">
            {texts?.invoiceOptions || "Invoice Options"}
          </Typography>

          <div className="mb-6">
            <Checkbox
              label={texts?.needInvoice || "I would like an invoice"}
              isOn={needsInvoice}
              onChange={(newValue) => setNeedsInvoice(newValue)}
              variant="default"
            />
          </div>

          {needsInvoice && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={texts?.companyName || "Company Name"}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={texts?.enterCompanyName || "Enter company name"}
                  required
                />

                <div>
                  <Input
                    label={texts?.taxId || "Tax ID (Optional)"}
                    value={taxId}
                    onChange={handleTaxIdChange}
                    placeholder={texts?.taxIdPlaceholder || "e.g., BG123456789"}
                    error={!!taxIdError}
                  />
                  {taxIdError && (
                    <p className="text-xs text-error mt-1">{taxIdError}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-text">
                  {texts?.billingAddress || "Billing Address"}
                </label>
                <AddressElement
                  options={{
                    mode: 'billing',
                    display: { name: 'split' }
                  }}
                  onChange={(event) => {
                    setAddressComplete(event.complete)
                    if (event.value?.address?.country) {
                      setCurrentCountry(event.value.address.country)
                      if (taxIdError) setTaxIdError(null)
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="hidden" disabled={!canContinue} />
      </form>

      <div style={{ display: 'none' }} data-can-continue={canContinue} data-missing-fields={missingFields.join(", ")} />
    </>
  )
}

export const InvoiceDetailsFooter = ({ onBack, onContinue, canContinue, missingFields, texts }) => (
  <div className="space-y-3">
    {missingFields && missingFields.length > 0 && (
      <div className="text-center">
        <p className="text-sm text-error">
          {texts?.requiredFields || "Required fields:"} {missingFields}
        </p>
      </div>
    )}

    <div className="flex justify-between space-x-3">
      <Button type="button" variant="secondary" onClick={onBack}>
        {texts?.back || "Back"}
      </Button>

      <Button type="button" variant="primary" disabled={!canContinue} onClick={onContinue}>
        {texts?.continue || "Continue"}
      </Button>
    </div>
  </div>
)

export default InvoiceDetailsDialog
